<template>
  <div class="flex flex-col gap-1">
    <BaseLabel
      forAttr="custom-message"
      text="Custom Message"
      :labelClasses="labelClasses"
    />
    <div class="flex gap-2 mb-2">
      <button
        type="button"
        @click="editor.chain().focus().toggleBold().run()"
        :class="[
          'px-2 py-1 rounded',
          editor.isActive('bold') ? 'font-bold text-blue-600 bg-blue-50' : '',
        ]"
        title="Bold"
      >
        <span class="font-bold">B</span>
      </button>
      <button
        type="button"
        @click="editor.chain().focus().toggleItalic().run()"
        :class="[
          'px-2 py-1 rounded',
          editor.isActive('italic') ? 'italic text-blue-600 bg-blue-50' : '',
        ]"
        title="Italic"
      >
        <span class="italic">I</span>
      </button>
      <button
        type="button"
        @click="editor.chain().focus().toggleStrike().run()"
        :class="[
          'px-2 py-1 rounded',
          editor.isActive('strike')
            ? 'line-through text-blue-600 bg-blue-50'
            : '',
        ]"
        title="Strikethrough"
      >
        <span class="line-through">S</span>
      </button>
      <button
        type="button"
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="[
          'px-2 py-1 rounded',
          editor.isActive('bulletList') ? 'text-blue-600 bg-blue-50' : '',
        ]"
        title="Bullet List"
      >
        • List
      </button>
      <button
        type="button"
        @click="editor.chain().focus().undo().run()"
        class="px-2 py-1 rounded"
        title="Undo"
      >
        ⎌
      </button>
      <button
        type="button"
        @click="editor.chain().focus().redo().run()"
        class="px-2 py-1 rounded"
        title="Redo"
      >
        ↻
      </button>
    </div>
    <EditorContent
      :editor="editor"
      class="tiptap border border-gray-200 rounded bg-gray-50 min-h-[120px] px-3 py-2 font-sans text-[18px] leading-7 [&_*]:font-sans [&_*]:text-[18px] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:mb-1"
    />
    <div class="text-xs text-right text-gray-400 mt-1">
      {{ charCount }} / 500 characters
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, watch, ref } from "vue";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import CharacterCount from "@tiptap/extension-character-count";
import BaseLabel from "@/components/shared/BaseLabel.vue";

export default defineComponent({
  name: "CustomMessageBox",
  components: { BaseLabel, EditorContent },
  props: {
    message: { type: String, required: true },
    labelClasses: { type: String, default: "" },
  },
  emits: ["update:message"],
  setup(props, { emit }) {
    const editor = new Editor({
      content: props.message,
      extensions: [StarterKit, CharacterCount.configure({ limit: 500 })],
      onUpdate({ editor }) {
        emit("update:message", editor.getHTML());
      },
    });

    // Character count using the extension
    const charCount = ref(editor.storage.characterCount.characters());

    // Watch for changes in the message prop and update editor content if needed
    watch(
      () => props.message,
      (newVal) => {
        if (editor.getHTML() !== newVal) {
          editor.commands.setContent(newVal || "");
        }
        charCount.value = editor.storage.characterCount.characters();
      },
      { immediate: true }
    );

    // Update charCount on editor update
    editor.on("update", () => {
      charCount.value = editor.storage.characterCount.characters();
    });

    onBeforeUnmount(() => {
      editor.destroy();
    });

    return { editor, charCount };
  },
});
</script>
